import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  List,
  CircularProgress,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

/**
 * AI Assistant Dialog Component
 * AI-based parameter suggestions for studies
 *
 * New workflow: User selects which suggestions to apply as filters (chips)
 *
 * @param {boolean} open - Whether dialog is open
 * @param {Function} onClose - Handler for closing dialog
 * @param {string} studyDescription - Study description input
 * @param {Function} setStudyDescription - Study description setter
 * @param {string} studyType - Study type input
 * @param {Function} setStudyType - Study type setter
 * @param {string} specialty - Specialty input
 * @param {Function} setSpecialty - Specialty setter
 * @param {Array} aiSuggestions - AI-generated suggestions
 * @param {Function} setAiSuggestions - AI suggestions setter
 * @param {Array} selectedSuggestions - Selected suggestion IDs
 * @param {Function} setSelectedSuggestions - Selected suggestions setter
 * @param {Function} handleGenerateSuggestions - Handler for generating suggestions
 * @param {Function} handleApplySuggestionsAsFilter - Handler for applying suggestions as filter
 * @param {boolean} isGeneratingSuggestions - Whether suggestions are being generated
 */
const AIAssistantDialog = ({
  open,
  onClose,
  studyDescription,
  setStudyDescription,
  studyType,
  setStudyType,
  specialty,
  setSpecialty,
  aiSuggestions,
  setAiSuggestions,
  selectedSuggestions,
  setSelectedSuggestions,
  handleGenerateSuggestions,
  handleApplySuggestionsAsFilter,
  isGeneratingSuggestions,
}) => {
  // Toggle handler for individual suggestions
  const handleToggleSuggestion = (suggestionId) => {
    setSelectedSuggestions(prev =>
      prev.includes(suggestionId)
        ? prev.filter(id => id !== suggestionId)
        : [...prev, suggestionId]
    );
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            KI-Assistent: Parameter-Vorschläge
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Beschreiben Sie Ihre Studie und erhalten Sie KI-gestützte Parameter-Empfehlungen
        </Typography>
      </DialogTitle>
      <DialogContent>
        {aiSuggestions.length === 0 ? (
          // Input Phase
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Studienbeschreibung"
              placeholder="Beschreiben Sie Ihre Studie... z.B. 'Retrospektive Kohortenstudie zur Untersuchung von Sepsis-Biomarkern bei ICU-Patienten. Ziel ist die Identifikation prädiktiver Marker für Mortalität.'"
              value={studyDescription}
              onChange={(e) => setStudyDescription(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Studientyp (optional)</InputLabel>
                  <Select
                    value={studyType}
                    onChange={(e) => setStudyType(e.target.value)}
                    label="Studientyp (optional)"
                  >
                    <MenuItem value="">Nicht angegeben</MenuItem>
                    <MenuItem value="retrospective">Retrospektiv</MenuItem>
                    <MenuItem value="prospective">Prospektiv</MenuItem>
                    <MenuItem value="rct">Randomisierte Studie</MenuItem>
                    <MenuItem value="observational">Beobachtungsstudie</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Fachrichtung (optional)</InputLabel>
                  <Select
                    value={specialty}
                    onChange={(e) => setSpecialty(e.target.value)}
                    label="Fachrichtung (optional)"
                  >
                    <MenuItem value="">Nicht angegeben</MenuItem>
                    <MenuItem value="icu">Intensivmedizin</MenuItem>
                    <MenuItem value="cardiology">Kardiologie</MenuItem>
                    <MenuItem value="nephrology">Nephrologie</MenuItem>
                    <MenuItem value="emergency">Notfallmedizin</MenuItem>
                    <MenuItem value="internal">Innere Medizin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        ) : (
          // Results Phase
          <Box sx={{ pt: 2 }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold">
                {aiSuggestions.length} Parameter vorgeschlagen
              </Typography>
              <Typography variant="caption">
                Wählen Sie die Parameter aus, die als Filter angewendet werden sollen
              </Typography>
            </Alert>

            <List>
              {aiSuggestions.map((suggestion) => (
                <Paper
                  key={suggestion.id}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    border: selectedSuggestions.includes(suggestion.id) ? '2px solid' : '1px solid',
                    borderColor: selectedSuggestions.includes(suggestion.id) ? 'primary.main' : 'divider',
                  }}
                  onClick={() => handleToggleSuggestion(suggestion.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Checkbox
                      checked={selectedSuggestions.includes(suggestion.id)}
                      onChange={() => handleToggleSuggestion(suggestion.id)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: 'primary.main',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        }
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <AutoAwesomeIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {suggestion.name}
                        </Typography>
                        <Chip label={suggestion.category} size="small" />
                        {suggestion.omopMapped && (
                          <Chip label="OMOP" size="small" color="success" />
                        )}
                        <Chip
                          label={`${suggestion.confidence}% Konfidenz`}
                          size="small"
                          color={suggestion.confidence >= 90 ? 'success' : 'default'}
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        {suggestion.reason}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </List>

            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setAiSuggestions([]);
                  setSelectedSuggestions([]);
                }}
              >
                Neue Suche
              </Button>
              <Typography variant="caption" color="textSecondary">
                {selectedSuggestions.length} von {aiSuggestions.length} ausgewählt
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        {aiSuggestions.length === 0 ? (
          <Button
            variant="contained"
            onClick={handleGenerateSuggestions}
            disabled={!studyDescription.trim() || isGeneratingSuggestions}
            startIcon={isGeneratingSuggestions ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
          >
            {isGeneratingSuggestions ? 'Analysiere...' : 'Parameter vorschlagen'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleApplySuggestionsAsFilter}
            startIcon={<AutoAwesomeIcon />}
            disabled={selectedSuggestions.length === 0}
          >
            Als Filter anwenden ({selectedSuggestions.length})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AIAssistantDialog;
